import { useEffect, useRef, useState } from 'react'
import { Alert, Button, Divider, Flex, Input, Progress, Typography, type GetRef } from 'antd'
import { QRCodeSVG } from 'qrcode.react'
import { useTranslation } from 'react-i18next'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { translateError } from '@/shared/api/errors'
import type { TotpOutcome } from '@/shared/auth/authApi'
import { useAuth } from '@/shared/auth/useAuth'
import { AuthShell } from './AuthShell'
import type { TenantChoiceState } from './TenantChoicePage'

/** Birinchi sozlash — QR ko'rsatiladi, tasdiqlash kodi kiritiladi (`totp/confirm`) */
export interface TotpSetupState {
  stage: 'setup'
  setupToken: string
  /** `otpauth://...` — QR shu yerdan MIJOZ TOMONDA chiziladi, sirni hech qayerga yubormaymiz */
  qrUri: string
  secret: string
  from: string
}

/** Har safargi kirish — faqat kod so'raladi (`totp/verify`) */
export interface TotpVerifyState {
  stage: 'verify'
  totpToken: string
  from: string
}

type TotpState = TotpSetupState | TotpVerifyState

type OtpRef = GetRef<typeof Input.OTP>

const TOTP_PERIOD_SECONDS = 30

/** TOTP kodlar har 30 soniyada yangilanadi — qolgan vaqtni ko'rsatish uchun */
function useTotpCountdown(): number {
  const [seconds, setSeconds] = useState(
    () => TOTP_PERIOD_SECONDS - (Math.floor(Date.now() / 1000) % TOTP_PERIOD_SECONDS),
  )
  useEffect(() => {
    const id = setInterval(() => {
      setSeconds(TOTP_PERIOD_SECONDS - (Math.floor(Date.now() / 1000) % TOTP_PERIOD_SECONDS))
    }, 1000)
    return () => clearInterval(id)
  }, [])
  return seconds
}

/**
 * TOTP ekrani — ikkala rejimni ham qamrab oladi:
 *   `setup`  — QR kod (mijoz tomonda chiziladi) + sirni qo'lda kiritish varianti
 *   `verify` — faqat 6 xonali kod
 * Ikkalasida ham bir xil kod maydoni: to'liq kiritilganda avtomatik yuboriladi.
 */
export function TotpPage() {
  const { t } = useTranslation()
  const { completeTotpSetup, completeTotpVerify } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const state = location.state as TotpState | null

  const [code, setCode] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const otpRef = useRef<OtpRef>(null)
  const secondsLeft = useTotpCountdown()

  // To'g'ridan-to'g'ri (masalan sahifa qayta yuklanib) ochilsa — oraliq
  // token yo'qolgan, qaytadan login qilish kerak
  if (!state) return <Navigate to="/login" replace />

  const submit = (value: string) => {
    // Yarim kiritilgan kodlarni va ikki marta yuborilishini (avto + Enter)
    // e'tiborsiz qoldiramiz
    if (value.length !== 6 || submitting) return

    setSubmitting(true)
    setError(null)

    const promise =
      state.stage === 'setup'
        ? completeTotpSetup(state.setupToken, value)
        : completeTotpVerify(state.totpToken, value)

    // Kod TO'G'RI bo'lishi natijani UCH XILDAN biriga olib kelishi mumkin —
    // bitta a'zolikda kirish shu yerda tugaydi, ko'p a'zolikda tashkilot
    // tanlash ekraniga o'tiladi (ENDI SHU YERDAN, TOTP'dan KEYIN), a'zolik
    // umuman yo'q bo'lsa esa aniq tushuntiruvchi ekranga yo'naltiriladi —
    // jimgina xato emas.
    const handleOutcome = (outcome: TotpOutcome) => {
      switch (outcome.kind) {
        case 'authenticated':
          navigate(state.from, { replace: true })
          return
        case 'chooseTenant': {
          const nextState: TenantChoiceState = {
            choiceToken: outcome.choiceToken,
            candidates: outcome.candidates,
            from: state.from,
          }
          navigate('/login/tenant', { state: nextState, replace: true })
          return
        }
        case 'noMembership':
          navigate('/login/no-access', { replace: true })
      }
    }

    void promise
      .then(handleOutcome)
      .catch((err: unknown) => {
        setError(translateError(err, t))
        // Xato bo'lsa maydon tozalanadi va fokus qaytadi
        setCode('')
        otpRef.current?.focus()
      })
      .finally(() => setSubmitting(false))
  }

  const isSetup = state.stage === 'setup'

  return (
    <AuthShell
      title={isSetup ? t('auth.totp.setupTitle') : t('auth.totp.verifyTitle')}
      subtitle={isSetup ? t('auth.totp.setupSubtitle') : t('auth.totp.verifySubtitle')}
    >
      {error ? <Alert type="error" showIcon message={error} /> : null}

      {isSetup ? (
        <Flex vertical align="center" gap={12}>
          {/* QR MIJOZ TOMONDA chiziladi (`qrcode.react`) — sir tashqi
              xizmatga hech qachon yuborilmaydi */}
          <div style={{ background: '#fff', padding: 12, borderRadius: 8, lineHeight: 0 }}>
            <QRCodeSVG value={state.qrUri} size={176} />
          </div>
          <Typography.Text type="secondary" style={{ textAlign: 'center' }}>
            {t('auth.totp.scanHint')}
          </Typography.Text>
          {/* ⚠️ QR O'ZGARMAYDI — VA BUNI AYTISH KERAK.
              Pastda 30 soniyalik taymer turibdi va u 6 xonali KODGA
              tegishli, QR ga emas. Lekin sozlash rejimida taymer QR
              ning ostida ko'rinadi va tester (2026-08-11, X-SIB-019)
              uni "QR yangilanishi kerak" deb o'qidi va yangilanmaganini
              nuqson deb yozdi.
              Ya'ni xato foydalanuvchida emas: interfeys ikki xil
              o'qiladigan holatda edi. Matn qo'shildi, mexanizm
              o'zgarmadi — sozlash siri bir marta beriladi va
              o'zgarmasligi TO'G'RI. */}
          <Typography.Text type="secondary" style={{ textAlign: 'center', fontSize: 12 }}>
            {t('auth.totp.qrStatic')}
          </Typography.Text>
          <Divider style={{ margin: '4px 0' }} plain>
            {t('auth.totp.manualEntry')}
          </Divider>
          <Typography.Text
            code
            copyable
            data-testid="totp-secret"
            style={{ wordBreak: 'break-all', textAlign: 'center', fontSize: 13 }}
          >
            {state.secret}
          </Typography.Text>
        </Flex>
      ) : null}

      <Flex vertical align="center" gap={8} style={{ marginTop: isSetup ? 20 : 0 }}>
        <Typography.Text>{t('auth.totp.enterCode')}</Typography.Text>
        <div data-testid="totp-otp-input">
          <Input.OTP
            ref={otpRef}
            length={6}
            size="large"
            value={code}
            onChange={(value) => {
              setCode(value)
              submit(value)
            }}
            disabled={submitting}
            autoFocus
          />
        </div>
        <Flex vertical align="center" gap={4} style={{ width: '100%', maxWidth: 220 }}>
          <Progress
            percent={(secondsLeft / TOTP_PERIOD_SECONDS) * 100}
            showInfo={false}
            size="small"
            status="active"
          />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('auth.totp.countdown', { seconds: secondsLeft })}
          </Typography.Text>
        </Flex>
      </Flex>

      <Button
        type="primary"
        block
        size="large"
        style={{ marginTop: 16 }}
        loading={submitting}
        disabled={code.length !== 6}
        onClick={() => submit(code)}
      >
        {t('common.done')}
      </Button>

      <Typography.Link onClick={() => navigate('/login', { replace: true })}>
        ← {t('auth.signIn')}
      </Typography.Link>
    </AuthShell>
  )
}
